import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play, CheckCircle } from 'lucide-react';
import { mockData } from '../data/mock';

const HeroSection = () => {
  const [currentStatIndex, setCurrentStatIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentStatIndex((prev) => (prev + 1) % mockData.hero.stats.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const scrollToProducts = () => {
    document.querySelector('#products').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-indigo-200/20 to-blue-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Floating geometric shapes */}
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-r from-blue-300/30 to-indigo-300/30 rounded-lg rotate-12 animate-bounce delay-300"></div>
        <div className="absolute top-3/4 right-1/4 w-24 h-24 bg-gradient-to-r from-indigo-300/30 to-blue-300/30 rounded-full animate-bounce delay-700"></div>
        <div className="absolute top-1/2 left-3/4 w-16 h-16 bg-gradient-to-r from-blue-400/40 to-indigo-400/40 rounded-lg rotate-45 animate-bounce delay-1000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className={`space-y-8 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <div className="space-y-6">
              <div className="flex items-center space-x-2 text-sm font-medium text-blue-600 bg-blue-100/80 px-4 py-2 rounded-full w-fit">
                <CheckCircle size={16} />
                <span>Government & Enterprise Solutions</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
                <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-500 bg-clip-text text-transparent">
                  Building
                </span>
                <br />
                Digital Excellence
                <br />
                <span className="text-4xl lg:text-5xl text-gray-600">for the Future</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-2xl">
                {mockData.hero.subtitle}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => window.open(mockData.hero.calendlyLink, '_blank')}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-blue-500/25 group"
              >
                {mockData.hero.cta}
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              
              <Button 
                variant="outline"
                size="lg"
                onClick={scrollToProducts}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 group"
              >
                <Play className="mr-2 h-5 w-5" />
                View Our Products
              </Button>
            </div>

            {/* Animated Stats */}
            <div className="pt-8 border-t border-gray-200">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {mockData.hero.stats.map((stat, index) => (
                  <div 
                    key={index}
                    className={`text-center transition-all duration-500 ${
                      currentStatIndex === index ? 'transform scale-110' : ''
                    }`}
                  >
                    <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm lg:text-base text-gray-600 font-medium">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Visual Element */}
          <div className={`relative transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
          }`}>
            <div className="relative">
              {/* Main visual container */}
              <div className="relative bg-gradient-to-br from-blue-100/50 to-indigo-100/50 rounded-3xl p-8 backdrop-blur-sm">
                <div className="space-y-6">
                  {/* Product logos showcase */}
                  <div className="grid grid-cols-2 gap-4">
                    {mockData.products.slice(0, 4).map((product, index) => (
                      <div 
                        key={index}
                        className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group"
                      >
                        <img 
                          src={product.logo} 
                          alt={product.name}
                          className="h-12 w-auto mx-auto mb-2 transition-transform group-hover:scale-110"
                        />
                        <p className="text-sm font-semibold text-gray-700 text-center">
                          {product.name}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Decorative elements */}
                  <div className="flex justify-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse delay-150"></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-300"></div>
                  </div>
                </div>
              </div>

              {/* Floating accent elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full animate-bounce"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r from-indigo-400 to-blue-400 rounded-full animate-bounce delay-500"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-blue-400 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-blue-400 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;